import { ref } from 'vue'
import { useAuthStore } from '@/stores/auth'

export function useAdmin() {
  const error = ref<string | null>(null)
  const success = ref<string | null>(null)

  const authStore = useAuthStore()

  const cleanupSystem = async () => {
    error.value = null
    success.value = null
    
    try {
      const csrfToken = await authStore.getCsrfToken()
      const response = await fetch('/api/processing-admin/cleanup_system/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken
        }
      })

      if (!response.ok) {
        const errorData = await response.json()
        error.value = `Error: ${JSON.stringify(errorData)}`
        return
      }

      success.value = 'System cleaned up successfully'
    } catch (err) {
      error.value = `Error: ${err instanceof Error ? err.message : String(err)}`
    }
  }

  const clearEventLog = async () => {
    error.value = null
    success.value = null
    
    try {
      const csrfToken = await authStore.getCsrfToken()
      const response = await fetch('/api/processing-admin/clear_event_log/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken
        }
      })

      if (!response.ok) {
        const errorData = await response.json()
        error.value = `Error: ${JSON.stringify(errorData)}`
        return
      }

      success.value = 'Event log cleared successfully'
    } catch (err) {
      error.value = `Error: ${err instanceof Error ? err.message : String(err)}`
    }
  }

  const createTestWorker = async () => {
    error.value = null
    success.value = null

    try {
      const csrfToken = await authStore.getCsrfToken()
      const userId = await authStore.get_pk()
      
      if (!userId) {
        error.value = 'Error: User not authenticated'
        return
      }
      
      const response = await fetch('/api/workers/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken
        },
        body: JSON.stringify({
          machine_id: 'test-worker-dummy',
          processing_software_version: '1.0.0',
          user: userId
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        error.value = `Error creating worker: ${JSON.stringify(errorData)}`
        return
      }

      success.value = 'Test worker created successfully'
    } catch (err) {
      error.value = `Error: ${err instanceof Error ? err.message : String(err)}`
    }
  }

  const updateTestWorker = async (workerId: number) => {
    error.value = null
    success.value = null

    try {
      const csrfToken = await authStore.getCsrfToken()
      const response = await fetch(`/api/workers/${workerId}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken
        },
        body: JSON.stringify({
          processing_software_version: '2.0.0'
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        error.value = `Error updating worker: ${JSON.stringify(errorData)}`
        return
      }

      success.value = 'Test worker updated successfully'
    } catch (err) {
      error.value = `Error: ${err instanceof Error ? err.message : String(err)}`
    }
  }

  const deleteTestWorker = async (workerId: number) => {
    error.value = null
    success.value = null

    try {
      const csrfToken = await authStore.getCsrfToken()
      const response = await fetch(`/api/workers/${workerId}/`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken
        }
      })

      if (!response.ok) {
        const errorData = await response.text()
        error.value = `Error deleting worker: ${errorData}`
        return
      }

      success.value = 'Test worker deleted successfully'
    } catch (err) {
      error.value = `Error: ${err instanceof Error ? err.message : String(err)}`
    }
  }

  return {
    error,
    success,
    cleanupSystem,
    clearEventLog,
    createTestWorker,
    updateTestWorker,
    deleteTestWorker
  }
} 